/**
 * PayloadServiceHelper
 *
 * Extracted from ChatbotexpandComponent to remove duplication around the
 * crosstab / brand-analysis "payload service" calls and the repeated
 * significance-payload dispatch logic used by the various dropdown handlers.
 *
 * Follows the existing `comp: any` helper pattern — the component keeps thin
 * wrapper methods that delegate here (passing `this` as `comp`), so all
 * existing call sites remain unchanged and behavior is preserved.
 */
export class PayloadServiceHelper {
  /**
   * Shared dispatch used by the dropdown/base/weight handlers: route to the
   * crosstab or brand-analysis payload service when an effective payload is
   * available, otherwise run the provided fallback (usually createTable).
   */
  static dispatchPayloadOrFallback(comp: any, target: any, fallback: () => void): void {
    const { effectiveCrosstabPayload, effectiveBrandPayload } = comp.resolveEffectivePayloads(target);

    if (comp.getMcode(target) === 3 && effectiveCrosstabPayload) {
      comp.callSignificancePayloadService(target.DefaultSampleSize || 'None', effectiveCrosstabPayload, target);
    } else if (comp.getMcode(target) === 2 && effectiveBrandPayload) {
      comp.callBrandAnalysisPayloadService(target.DefaultSampleSize || 'None', effectiveBrandPayload, target);
    } else {
      fallback();
    }
  }

  /**
   * Handles the specialized "Previous Year" / "Previous Period" / "None" significance
   * dispatch shared by the sample-size and confidence-level dropdown handlers.
   * Returns `true` when the selection was handled via a payload service call, so the
   * caller can early-return; returns `false` for all other selections.
   *
   * `onBeforeCall` runs just before a payload service is invoked (used by the
   * sample-size handler to close its dropdown only when a payload call is made).
   */
  static dispatchSpecialSignificance(comp: any, target: any, sampleSizeValue: string, onBeforeCall?: () => void): boolean {
    const isPreviousYear = sampleSizeValue === 'Previous Year' || (!!sampleSizeValue && sampleSizeValue.includes('Previous Year'));
    const isPreviousPeriod = sampleSizeValue === 'Previous Period' || (!!sampleSizeValue && sampleSizeValue.includes('Previous Period'));
    const isNone = sampleSizeValue === 'None';

    if (!(isPreviousYear || isPreviousPeriod || isNone)) {
      return false;
    }

    // Resolve correct payload based on target context (Current session vs Historical Block)
    const { effectiveCrosstabPayload, effectiveBrandPayload } = comp.resolveEffectivePayloads(target);
    const significanceType = isNone ? 'None' : (isPreviousYear ? 'Previous Year' : 'Previous Period');

    if (comp.getMcode(target) == 3 && effectiveCrosstabPayload) {
      if (onBeforeCall) onBeforeCall();
      comp.callSignificancePayloadService(significanceType, effectiveCrosstabPayload, target);
      return true;
    }

    if (comp.getMcode(target) == 2 && effectiveBrandPayload) {
      if (onBeforeCall) onBeforeCall();
      comp.callBrandAnalysisPayloadService(significanceType, effectiveBrandPayload, target);
      return true;
    }

    return false;
  }

  static async callSignificancePayloadService(comp: any, significanceType: string, payload: any, block: any = null): Promise<void> {
    const target = block || comp;
    target.isTableLoading = true;
    try {
      const token = await comp.getBearerToken();
      const requestPayload = {
        bearer_token: token,
        session_id: comp.guidChatId,
        client_name: comp.selected_client,
        study: comp.selected_study,
        payload: payload,
        Significance: significanceType,
        SignificanceVsReference: target.SignificanceVsReference || "",
        "Confidence Level": target.DefaultConfidenceLevel,
        Base: target.DefaultBase,
        Weight: target.selectedWeight
      };


      comp.apiService.crosstabPayloadService(requestPayload).subscribe({
        next: (res: any) => {
          comp.processPayloadServiceResponse(res, block);
          target.isTableLoading = false;
        },
        error: (err: any) => {
          console.error('[SIGNIFICANCE-ERROR] crosstab-payload-service failed:', err);
          target.isTableLoading = false;
        }
      });
    } catch (e) {
      console.error('Exception in Significance specialized call:', e);
      target.isTableLoading = false;
    }
  }

  static async callBrandAnalysisPayloadService(comp: any, significanceType: string, payload: any, block: any = null): Promise<void> {
    const target = block || comp;
    target.isTableLoading = true;
    try {
      const token = await comp.getBearerToken();
      const requestPayload = {
        bearer_token: token,
        session_id: comp.guidChatId,
        client_name: comp.selected_client,
        study: comp.selected_study,
        payload: payload,
        Significance: significanceType,
        SignificanceVsReference: target.SignificanceVsReference || "",
        "Confidence Level": target.DefaultConfidenceLevel,
        Base: target.DefaultBase,
        Weight: target.selectedWeight
      };

      comp.apiService.brandAnalysisPayloadService(requestPayload).subscribe({
        next: (res: any) => {
          comp.processPayloadServiceResponse(res, block);
          target.isTableLoading = false;
        },
        error: (err: any) => {
          console.error('Error calling brand-analysis-payload-service:', err);
          target.isTableLoading = false;
        }
      });
    } catch (e) {
      console.error('Exception in Brand Analysis Significance specialized call:', e);
      target.isTableLoading = false;
    }
  }

  static processPayloadServiceResponse(comp: any, res: any, block: any = null): void {
    const target = block || comp;

    if (res && res.response) {
      let resultData = res.response;

      // The backend might return a result object that has a 'Data' string or direct object
      let parsedData: any[] = [];
      try {
        if (resultData.Data) {
          parsedData = typeof resultData.Data === 'string' ? JSON.parse(resultData.Data) : resultData.Data;
        } else {
          parsedData = typeof resultData === 'string' ? JSON.parse(resultData) : resultData;
        }
      } catch (e) {
        console.error('[SIGNIFICANCE-ERROR] Error parsing response data:', e);
        return;
      }

      if (!Array.isArray(parsedData)) {
        console.error('[SIGNIFICANCE-ERROR] Response data is not an array:', parsedData);
        return;
      }


      target.CrossTabOutPutEntireData = parsedData;
      comp.normalizeNestedChildFields(target.CrossTabOutPutEntireData);
      target.CrossTabOutPutData = (target.isTotal ?? comp.isTotal)
        ? target.CrossTabOutPutEntireData
        : target.CrossTabOutPutEntireData.filter((x: any) => !x.isTotal);

      comp.BindTableOutput(target.CrossTabOutPutData, block);

      // Sort the target's data object
      if (target.OutputdataObject && target.OutputdataObject.Row) {
        comp.sortOutputData(block);
      }

      if (target.isTranspose) {
        comp.handleOutputdataObjectOnTransposeSelect(block);
      }

      // Sync to shared service for components still using it (like SidePanel)
      comp.crossTabService.setCrossTabOuputData(target.CrossTabOutPutData);
      comp.updateChartDisablingFlags(target);

      // Trigger chart updates for the active view
      const chartType = target.isTranspose ? 'column' : 'bar';
      if (target.isLineChartClick) {
        comp.onTrendChartIconClick(block);
      } else if (target.isVerticalGroupBarChartClick || target.isVerticalBarChartClick) {
        comp.VerticalChartClick(chartType, block);
      } else if (target.isVerticalStackChartClick) {
        comp.onVerticalStackBarChartIconClick(chartType, block);
      }
    }
  }
}
